import { useReferenceStore } from "@app/store/useReferenceStore";

/**
 * Имена типов СО, используемые в UI.
 * Реальные коды приходят из `so.loadSOTypeListByFilter` и попадают в {@link useReferenceStore} при bootstrap.
 *
 * Список не обязан покрывать весь справочник API — только имена, к которым обращается код.
 *
 * Для БО код `soType` обычно совпадает с `botype`, но это разные сущности и разные справочники.
 */
export type SoTypeName =
    | "Акт инвентаризации"
    | "Акт инвентаризации УП"
    | "Внутризаводская накладная"
    | "Внутризаводская накладная УП"
    | "Лимитная карта"
    | "Отпуск по лимитной карте";

function getSoTypeMap(): Record<string, number> {
    const types = useReferenceStore.getState().soTypes;
    return Object.fromEntries(types.map((t) => [t.Name, t.Code]));
}

/**
 * Коды типов СО по имени. Данные берутся из {@link useReferenceStore} в момент обращения.
 * Хук стора в компонентах не нужен: используйте `SoType["Лимитная карта"]` и т.д.
 * Если справочник ещё не загружен при bootstrap, обращение по ключу выбросит ошибку.
 *
 * @example
 * ```ts
 * switch (soType) {
 *   case SoType["Лимитная карта"]:
 *     ...
 * }
 * ```
 */
export const SoType: { [K in SoTypeName]: number } = new Proxy({} as { [K in SoTypeName]: number }, {
    get(_, prop: string) {
        const map = getSoTypeMap();
        const value = map[prop];
        if (value === undefined) {
            throw new Error(
                `SoType["${prop}"]: справочник типов СО не загружен или тип "${prop}" отсутствует. Проверьте bootstrap и наличие данных в useReferenceStore.`,
            );
        }
        return value;
    },
});

/**
 * Наименование типа СО по коду (данные из {@link useReferenceStore}).
 */
export function getSoTypeName(code: number): string {
    const item = useReferenceStore.getState().soTypes.find((t) => t.Code === code);
    return item?.Name ?? "";
}
